import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { approvalsApi } from '../api/approvals';
import { useAuth } from '../hooks/useAuth';

interface PendingApprovalsValue {
  /** Number of maker-checker requests awaiting review. 0 while unknown. */
  count: number;
  /** Re-fetch immediately — call after submitting or reviewing a request. */
  refresh: () => Promise<void>;
}

const PendingApprovalsContext = createContext<PendingApprovalsValue>({
  count: 0,
  refresh: async () => {},
});

const POLL_INTERVAL_MS = 60_000;

export const PendingApprovalsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, can } = useAuth();
  const [count, setCount] = useState(0);

  // The list endpoint requires approvals.view; without it the poll would
  // just 403 forever, so don't start it.
  const canFetch = isAuthenticated && can('approvals.view');

  const refresh = useCallback(async () => {
    if (!canFetch) {
      setCount(0);
      return;
    }
    try {
      const { total } = await approvalsApi.list({ status: 'pending', limit: 1 });
      setCount(total);
    } catch {
      // Badge is best-effort; leave the last known count on transient errors.
    }
  }, [canFetch]);

  useEffect(() => {
    if (!canFetch) {
      setCount(0);
      return;
    }
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [canFetch, refresh]);

  return (
    <PendingApprovalsContext.Provider value={{ count, refresh }}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export const usePendingApprovals = () => useContext(PendingApprovalsContext);
